import { h } from "preact";
import uniq from "lodash/uniq";

import i18n from "../../i18n.js";

import InfoLine from "../InfoLine.jsx";
import TextField from "../TextField.jsx";
import RadioButtonGroup from "../RadioButtonGroup.jsx";
import Slider from "../Slider.jsx";
import SkillChooser from "../questions/SkillChooser.jsx";
import Combobox from "../questions/Combobox.jsx";
import MultiSelect from "../MultiSelect.jsx";
import UserSelector from "../UserSelector.jsx";
import { useSelector } from "react-redux";
import { selectCurrentUserId } from "../../store/currentUserSlice.js";

export default function ProjectWizardStep1({
  config,
  editMode,
  form,
  formErrors,
}) {
  const currentUserId = useSelector(selectCurrentUserId);
  const { fieldsFor, setFormDataValue } = form;

  const areaOfWorkOptions = config.areasOfWork.map(([label, value]) => ({
    label,
    value,
  }));

  const workplaceTypeOptions = config.workplaceTypes.map(([label, value]) => ({
    label,
    value,
  }));

  const salaryFieldOptions = fieldsFor("salary", {
    serverErrors: formErrors.salary,
  });
  salaryFieldOptions.onChange = salaryFieldOptions.onInput;

  const locationsFieldOptions = fieldsFor("locations", {
    serverErrors: formErrors.locations,
  });
  const languagesFieldOptions = fieldsFor("languages", {
    serverErrors: formErrors.languages,
  });
  languagesFieldOptions.answer = languagesFieldOptions.value;

  const employmentTypeFieldOptions = fieldsFor("employmentType", {
    serverErrors: formErrors.employmentType,
  });
  employmentTypeFieldOptions.answer = employmentTypeFieldOptions.value;

  const recruitersFieldOptions = fieldsFor("recruiters", {
    serverErrors: formErrors.recruiters,
  });

  return (
    <div>
      <div class="row">
        <div class="col s12 m10 l8 mb-32">
          {editMode ? (
            <h1 class="t-h5 t-h5--margin mb-8">
              {i18n.t("component.projects_add.projects_wizard.edit.title")}
            </h1>
          ) : (
            <h1 class="t-h5 t-h5--margin">
              {i18n.t("component.projects_add.projects_wizard.step1.title")}
            </h1>
          )}
          {!editMode && (
            <p class="t-body3 color-shades-charcoal mb-32">
              {i18n.t("component.projects_add.projects_wizard.step1.subtitle")}
            </p>
          )}

          <UserSelector
            class="mb-32 mt-32"
            recruiters={config.recruiters}
            placeholder={i18n.t(
              "component.projects_add.projects_wizard.step1.recruiters.search"
            )}
            label={i18n.t(
              "component.projects_add.projects_wizard.step1.recruiters.label"
            )}
            {...recruitersFieldOptions}
          />
          {recruitersFieldOptions.value.indexOf(currentUserId) === -1 && (
            <InfoLine
              iconVariant="warning"
              text={i18n.t(
                "component.projects_add.projects_wizard.step1.recruiters.unassigned_info"
              )}
            />
          )}

          {!editMode && (
            <h3 class="t-h6 t-h6--margin ">
              {i18n.t(
                "component.projects_add.projects_wizard.step1.jobTitle.title"
              )}
            </h3>
          )}
          <TextField
            {...fieldsFor("jobTitle", {
              serverErrors: formErrors.jobTitle,
            })}
            label={i18n.t(
              "component.projects_add.projects_wizard.step1.jobTitle.label"
            )}
            info={i18n.t(
              "component.projects_add.projects_wizard.step1.jobTitle.info"
            )}
          />
        </div>

        <div class="col s12 m10 l8 mb-32">
          <RadioButtonGroup
            {...fieldsFor("areaOfWork", {
              serverErrors: formErrors.areaOfWork,
            })}
            items={areaOfWorkOptions.map((opt) => ({
              ...opt,
              tooltip: i18n.t(
                `talent_filters.area_of_work.${opt.value}.description`
              ),
            }))}
            twoColumns
          />
        </div>

        <div class="col s12 m10 l8 mb-32">
          <TextField
            {...fieldsFor("companyName", {
              serverErrors: formErrors.companyName,
            })}
            label={i18n.t(
              "component.projects_add.projects_wizard.step1.companyName.label"
            )}
          />
        </div>

        <div class="col s12 m10 l8 mb-32">
          <h3 class="t-subtitle1 t-subtitle1--margin-small">
            {i18n.t(
              "component.projects_add.projects_wizard.step1.locations.title"
            )}
          </h3>
          <p class="t-body3 color-shades-charcoal mb-32">
            {i18n.t(
              "component.projects_add.projects_wizard.step1.locations.subtitle"
            )}
          </p>

          <MultiSelect
            {...locationsFieldOptions}
            label={i18n.t(
              "component.projects_add.projects_wizard.step1.locations.label"
            )}
            onChange={(addedValue) => {
              const result = uniq([...locationsFieldOptions.value, addedValue]);
              setFormDataValue("locations", result);
            }}
            onRemove={(removedValue) => {
              const result = locationsFieldOptions.value.filter(
                (x) => x != removedValue
              );
              setFormDataValue("locations", result);
            }}
            values={locationsFieldOptions.value.sort()}
            suggestions={config.cities.map((value) => ({ text: value, value }))}
          />
        </div>

        <div class="col s12 m10 l8 mb-32">
          <RadioButtonGroup
            {...fieldsFor("workplaceType", {
              serverErrors: formErrors.workplaceType,
            })}
            items={workplaceTypeOptions.map((opt) => ({
              ...opt,
              tooltip: i18n.t(
                `options.workplace_type_explanation.${opt.value}`,
                ""
              ),
            }))}
          />
        </div>

        <div class="col s12 m10 l8 mb-32">
          <h3 class="t-subtitle1 t-subtitle1--margin-small">
            {i18n.t(
              "component.projects_add.projects_wizard.step1.employmentType.title"
            )}
          </h3>
          <p class="t-body3 color-shades-charcoal mb-32">
            {i18n.t(
              "component.projects_add.projects_wizard.step1.employmentType.subtitle"
            )}
          </p>

          <Combobox
            {...employmentTypeFieldOptions}
            options={{
              suggestions: config.employmentTypes,
              onlySuggestions: true,
              label: i18n.t(
                "component.projects_add.projects_wizard.step1.employmentType.label"
              ),
            }}
            onSelect={(value) => setFormDataValue("employmentType", value)}
          />
        </div>
        <div class="col s12 m10 l8 mb-32">
          <h3 class="t-subtitle1 t-subtitle1--margin-small">
            {i18n.t(
              "component.projects_add.projects_wizard.step1.salary.title"
            )}
          </h3>
          <p class="t-body3 color-shades-charcoal mb-32">
            {i18n.t(
              "component.projects_add.projects_wizard.step1.salary.subtitle"
            )}
          </p>
          <Slider
            {...salaryFieldOptions}
            range
            possibleValues={config.compensationRange.reduce(
              (mem, [text, key]) => {
                mem[key] = text;
                return mem;
              },
              {}
            )}
          />
        </div>

        <div class="col s12 m10 l8 mb-32">
          <h3 class="t-subtitle1 t-subtitle1--margin-small">
            {i18n.t(
              "component.projects_add.projects_wizard.step1.languages.title"
            )}
          </h3>
          <p class="t-body3 color-shades-charcoal mb-32">
            {i18n.t(
              "component.projects_add.projects_wizard.step1.languages.subtitle"
            )}
          </p>

          <SkillChooser
            {...languagesFieldOptions}
            onSelect={(value) => setFormDataValue("languages", value)}
            options={{
              likert: config.languageProficiencies.length,
              choices: config.languages,
              proficiencies: config.languageProficiencies,
              label: i18n.t(
                "component.projects_add.projects_wizard.step1.languages.label"
              ),
              valueI18nPrefix:
                "component.projects_add.projects_wizard.step1.languages.values.",
            }}
          />
        </div>
      </div>
    </div>
  );
}
